/**
 * <s-product-gallery> — PDP main image with selectable thumbnails.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal snap-scroll track showing one slide at a time.
 *   [data-gallery-slide]          — direct children of the track, one per media item.
 *   button[data-gallery-thumb="i"] — thumbnail that shows slide i; aria-current marks the active one.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #track;
  #slides = [];
  #thumbs = [];

  connectedCallback() {
    this.#ac = new AbortController();
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;

    this.#slides = Array.from(this.#track.querySelectorAll(':scope > [data-gallery-slide]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

    this.addEventListener('click', this.#handleThumbClick, { signal: this.#ac.signal });

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.#track,
      threshold: 0.6,
    });
    for (const slide of this.#slides) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleThumbClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb || !this.contains(thumb)) return;

    const index = Number(thumb.dataset.galleryThumb);
    const slide = this.#slides[index];
    if (!slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({
      left: slide.offsetLeft,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    this.#setActive(index);
  };

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.#slides.indexOf(entry.target);
      if (index !== -1) this.#setActive(index);
    }
  };

  #setActive(index) {
    this.#thumbs.forEach((thumb, i) => {
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
